import { useJobillaOrgId } from '@/hooks'
import { useAtom, useAtomValue } from 'jotai'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '../../../rapid-ui'
import { ConfirmationDialog, ConfirmationDialogRef } from '../../../rapid-ui/structural'
import { logger } from '../../../utilities'
import { useBrandLibraryStore } from './brand-library-store'
import { SmallBrandingCard } from './small-card'
import {
	brandLibraryAtom,
	deleteBrandingCallbackAtom,
	deletingBrandingIdAtom,
	expandedBrandingIdAtom,
	newBrandingCallbackAtom,
} from './state'

export type BrandLibraryListProps = {
	campaignId?: string
}

export function BrandLibraryList({ campaignId }: BrandLibraryListProps) {
	const { t } = useTranslation('common')
	const store = useBrandLibraryStore()
	const brandings = useAtomValue(brandLibraryAtom, { store })
	const organisationId = useJobillaOrgId()

	const [expandedBrandingId, setExpandedBrandingId] = useAtom(expandedBrandingIdAtom, { store })
	const newBrandingCallback = useAtomValue(newBrandingCallbackAtom, { store })
	const deleteBrandingCallback = useAtomValue(deleteBrandingCallbackAtom, { store })
	const deletingBrandingId = useAtomValue(deletingBrandingIdAtom, { store })

	const deleteDialogRef = useRef<ConfirmationDialogRef>(null)

	const [isPendingNewBranding, setIsPendingNewBranding] = useState(false)
	const [pendingBrandingId, setPendingBrandingId] = useState<string | null>(null)

	const handleDeleteBranding = useCallback(async (brandingId: string) => {
		try {
			await deleteDialogRef.current?.confirm({
				title: t('branding.deleteTitle'),
				message: t('branding.deleteMessage'),
				confirmText: t('branding.deleteConfirm'),
				cancelText: t('common.cancel'),
				destructive: true,
			})
		} catch {
			return
		}

		deleteBrandingCallback?.(brandingId, () => {
			if (expandedBrandingId === brandingId) {
				setExpandedBrandingId(null)
			}
		})
	}, [deleteBrandingCallback, expandedBrandingId, setExpandedBrandingId, t])

	const handleNewBranding = useCallback(() => {
		if (campaignId && organisationId) {
			logger.track('Brand library: Clicked new', {
				data: {
					campaign_id: campaignId,
					organisation_id: organisationId,
					company: organisationId,
				},
			})
		}

		setIsPendingNewBranding(true)
		newBrandingCallback?.((brandingId) => {
			setPendingBrandingId(brandingId)
		})
	}, [newBrandingCallback, campaignId, organisationId])

	// if there's a pending brand id, and it's in the brandings list, set it as the expanded branding
	useEffect(() => {
		if (pendingBrandingId && brandings.some((branding) => branding.id === pendingBrandingId)) {
			setExpandedBrandingId(pendingBrandingId)
			// and clear
			setPendingBrandingId(null)
			setIsPendingNewBranding(false)
		}
	}, [pendingBrandingId, brandings])

	useEffect(() => {
		if (expandedBrandingId === null && brandings.length > 0) {
			setExpandedBrandingId(brandings[0].id)
		}
	}, [expandedBrandingId, brandings])

	return (
		<div className="grid grid-cols-1 gap-4 p-4 w-full min-w-0">
			<Button
				fullWidth
				size="short"
				type="committal"
				onClick={handleNewBranding}
				prefix="plus"
				isLoading={isPendingNewBranding}
			>
				New set
			</Button>
			{brandings.map((branding) => (
				<SmallBrandingCard
					key={branding.id}
					branding={branding}
					selected={branding.id === expandedBrandingId}
					onSelect={() => setExpandedBrandingId(branding.id)}
					onDelete={deleteBrandingCallback ? handleDeleteBranding : undefined}
					isDeleting={branding.id === deletingBrandingId}
				/>
			))}
			<ConfirmationDialog ref={deleteDialogRef} />
		</div>
	)
}
